import axios from 'axios';

const api = axios.create({
  baseURL: 'https://api.cuhkcollegehat.com',
});

// 请求拦截器:
// - /api/admin/* 走管理员 token(localStorage.admin_token)
// - 其它(学生接口)走学生 token(localStorage.student_token)
// 两者分开存,避免同一浏览器里管理员/学生身份互相污染。
api.interceptors.request.use((config) => {
  const url = config.url || '';
  const isAdmin = url.startsWith('/api/admin');
  const token = isAdmin
    ? localStorage.getItem('admin_token')
    : localStorage.getItem('student_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ 学生身份 token 辅助 ============
export const studentAuth = {
  set: (token: string) => localStorage.setItem('student_token', token),
  clear: () => localStorage.removeItem('student_token'),
  get: () => localStorage.getItem('student_token'),
};

// ============ 访客(guest)API —— 无状态,不落库,不带 token ============
export const guestApi = {
  /** 提交答案,一次性算分返回(九个书院);不写库、不需要登录 */
  score: (rankings: { question_id: number; option_rankings: number[] }[]) =>
    api.post('/api/guest/score', { rankings }),
};

// ============ 室友配对相关 API ============
export const roommateApi = {
  /** 发起配对邀请 */
  invite: (userId: number, partnerStudentId: string) =>
    api.post('/api/roommate/invite', {
      user_id: userId,
      partner_student_id: partnerStudentId,
    }),

  /** 接受配对邀请(被邀请方调用) */
  accept: (userId: number, pairId: number) =>
    api.post('/api/roommate/accept', {
      user_id: userId,
      pair_id: pairId,
    }),

  /** 拒绝配对邀请(被邀请方调用) */
  reject: (userId: number, pairId: number) =>
    api.post('/api/roommate/reject', {
      user_id: userId,
      pair_id: pairId,
    }),

  /** 解除已确认配对 / 撤回未接受的邀请 */
  dissolve: (userId: number, pairId: number) =>
    api.post('/api/roommate/dissolve', {
      user_id: userId,
      pair_id: pairId,
    }),

  /** 查询当前用户的配对状态 */
  status: (userId: number) =>
    api.get(`/api/roommate/status/${userId}`),

  /** 配对预览(必须已 confirmed),返回双方分数 + 合并分数 */
  preview: (userId: number) =>
    api.get(`/api/roommate/preview/${userId}`),
};

// ============ 名单管理相关 API(管理员) ============
export const rosterApi = {
  /** 获取完整名单(含答题/配对/分配状态) */
  list: () => api.get('/api/admin/roster'),

  /** 上传 Excel 名单(新增或更新,不删除) */
  upload: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/admin/roster/upload', fd);
  },

  /** 添加一名学生 */
  add: (data: { student_id: string; last_name: string; first_name: string; gender: string }) =>
    api.post('/api/admin/roster/student', data),

  /** 编辑学生信息 */
  update: (id: number, data: Record<string, string | undefined>) =>
    api.put(`/api/admin/roster/student/${id}`, data),

  /** 删除学生(force=true 时连同其答题/配对/分配数据一起删) */
  remove: (id: number, force = false) =>
    api.delete(`/api/admin/roster/student/${id}`, { params: { force } }),

  /** 导出当前名单为 Excel */
  exportFile: () => api.get('/api/admin/roster/export', { responseType: 'blob' }),
};

export default api;
